import {
  API_CALL, BEDBANK_UPDATE_PRICING_DATA,
} from './actionConstants'
import {
  FETCH_BEDBANK_OFFER, FETCH_BEDBANK_OFFER_RATES,
  FETCH_BEDBANK_OFFERS_RATES,
  FETCH_BEDBANK_OFFER_CALENDAR,
  FETCH_BEDBANK_OFFER_RATES_BULK,
} from './apiActionConstants'

import * as OfferService from 'api/offer'
import { isBedbankOfferCached } from 'selectors/bedbankSelectors'
import type { AppAction } from './types/ActionTypes'
import NotFound from '../api/errors/NotFound'
import { getAccumulatedBedbankRatesById, getRatesForBedbankOffer, getRatesForBedbankOffers } from 'api/bedbankRates'
import { getCalendar } from 'api/bedbank'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { getBedbankRatesCheckInRange } from '../lib/hotels/bedbankUtils'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import { getSourceForProperty, isPropertyCollectEnabled } from 'selectors/offerSelectors'
import { getChannelMarkupSource } from 'selectors/channelMarkupSelector'
import { clearOfferCommissionData } from './AgentHubCommissionActions'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
export function fetchBedbankOfferById(
  offerId: string,
  options?: {
    regionCode?: string
    flightOrigin?: string
    isSpoofed?: boolean
  },
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const source = getSourceForProperty(state, offerId)

    if (isBedbankOfferCached(state, offerId)) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_BEDBANK_OFFER,
      request: async() => {
        const offer = await OfferService.getOfferById(offerId, {
          region: options?.regionCode || state.geo.currentRegionCode,
          preview: state.system.previewMode ? state.system.previewHash : undefined,
          flightOrigin: options?.flightOrigin || getDefaultAirportCode(state),
          isSpoofed: options?.isSpoofed,
          source,
          markupSource: getChannelMarkupSource(state),
        })
        if (offer.type === 'bedbank_hotel') {
          return offer
        }

        throw new NotFound(`Bedbank offer ${offerId} is not found`)
      },
      offerId,
    })
  }
}

export function convertBedbankOfferToSummary(offer: App.BedbankOffer): App.BedbankOfferSummary {
  const {
    description,
    metaDescription,
    attractions,
    facilityGroups,
    ...offerSummary
  } = offer
  return offerSummary
}

export function fetchAccumulatedRatesForBedbankOffer(
  id: string,
  rooms: Array<App.Occupants>,
  checkIn: string,
  checkOut: string,
  refresh?: boolean,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const enablePropertyCollect = isPropertyCollectEnabled(state)

    const filterKey = getBedbankRateKey(rooms, checkIn, checkOut)
    if (!refresh && (state.offer.bedbankOfferRates[id]?.[filterKey] || state.offer.bedbankOfferRatesLoading[id]?.[filterKey])) {
      // already have fetched the rooms for the current filters
      return
    }

    const authState: App.AuthState = state.auth
    const source = getSourceForProperty(state, id)

    const requestData = {
      checkIn,
      checkOut,
      rooms,
      region: state.geo.currentRegionCode,
      id,
      timezone: state.offer.bedbankOffers[id]?.property.timezone,
      preview: state.system.previewMode ? state.system.previewHash : undefined,
      isSpoofed: authState.account.isSpoofed,
      source,
      markupSource: getChannelMarkupSource(state),
    }

    if (isAgentHubEnabled) {
      dispatch(clearOfferCommissionData(id))
    }

    dispatch({
      type: API_CALL,
      api: FETCH_BEDBANK_OFFER_RATES,
      request: () => getAccumulatedBedbankRatesById(requestData, enablePropertyCollect),
      offerId: id,
      filterKey,
    })
  }
}

export function fetchRatesForBedbankOffer(
  offer: App.BedbankOffer | App.BedbankOfferSummary,
  rooms: Array<App.Occupants>,
  checkIn: string,
  checkOut: string,
  refresh?: boolean,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const filterKey = getBedbankRateKey(rooms, checkIn, checkOut)
    if (!refresh && (state.offer.bedbankOfferRates[offer.id]?.[filterKey] || state.offer.bedbankOfferRatesLoading[offer.id]?.[filterKey])) {
      // already have fetched the rooms for the current filters
      return
    }

    const authState: App.AuthState = state.auth
    const source = getSourceForProperty(state, offer.id)

    const requestData = {
      checkIn,
      checkOut,
      rooms,
      region: state.geo.currentRegionCode,
      id: offer.id,
      timezone: offer.property.timezone,
      preview: state.system.previewMode ? state.system.previewHash : undefined,
      isSpoofed: authState.account.isSpoofed,
      source,
      markupSource: getChannelMarkupSource(state),
    }

    if (isAgentHubEnabled) {
      dispatch(clearOfferCommissionData(offer.id))
    }

    dispatch({
      type: API_CALL,
      api: FETCH_BEDBANK_OFFER_RATES,
      request: () => getRatesForBedbankOffer(requestData),
      offerId: offer.id,
      filterKey,
    })
  }
}

export function fetchRatesRangeForBedbankOffer(
  offerId: string,
  rooms: Array<App.Occupants>,
  monthKey: string,
  duration: number,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const source = getSourceForProperty(state, offerId)

    const checkInsRange = getBedbankRatesCheckInRange(monthKey, duration)
    if (state.offer.offerRatesLoading[offerId]) {
      return
    }

    const ratesToFetch = checkInsRange.filter(({ checkIn, checkOut }) => {
      const filterKey = getBedbankRateKey(rooms, checkIn, checkOut)
      if (state.offer.bedbankOfferRates[offerId]?.[filterKey]) {
        // already have fetched the rooms for the current filters
        return false
      }

      return true
    })

    if (!ratesToFetch.length) {
      return
    }

    const authState: App.AuthState = state.auth

    dispatch({
      type: API_CALL,
      api: FETCH_BEDBANK_OFFER_RATES_BULK,
      request: async() => {
        return await Promise.all(ratesToFetch.map(async({ checkIn, checkOut }) => {
          const filterKey = getBedbankRateKey(rooms, checkIn, checkOut)
          const enablePropertyCollect = isPropertyCollectEnabled(state)

          const requestData = {
            checkIn,
            checkOut,
            rooms,
            region: state.geo.currentRegionCode,
            preview: state.system.previewMode ? state.system.previewHash : undefined,
            isSpoofed: authState.account.isSpoofed,
            source,
            markupSource: getChannelMarkupSource(state),
          }

          const response = await getRatesForBedbankOffers([offerId], requestData, enablePropertyCollect)

          return {
            filterKey,
            rates: response.flatMap(r => r.rates),
          }
        }))
      },
      offerId,
    })
  }
}

export function fetchRatesForBedbankOffersById(
  offerIds: Array<string>,
  rooms: Array<App.Occupants>,
  checkIn: string,
  checkOut: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const filterKey = getBedbankRateKey(rooms, checkIn, checkOut)
    const missingOfferIds = offerIds.filter(id => !state.offer.bedbankOfferRates[id]?.[filterKey] && !state.offer.bedbankOfferRatesLoading[id]?.[filterKey])
    if (missingOfferIds.length === 0) {
      // already have fetched the rooms for the current filters
      return
    }

    const authState: App.AuthState = state.auth
    const enablePropertyCollect = isPropertyCollectEnabled(state)

    // Group offer IDs by their per-property source so blacklisted
    // properties use B2C rates while others keep the page-level source.
    const idsBySource = new Map<string | undefined, Array<string>>()
    for (const id of missingOfferIds) {
      const source = getSourceForProperty(state, id)
      const group = idsBySource.get(source)
      if (group) {
        group.push(id)
      } else {
        idsBySource.set(source, [id])
      }
    }

    for (const [source, ids] of idsBySource) {
      const requestData = {
        checkIn,
        checkOut,
        rooms,
        region: state.geo.currentRegionCode,
        preview: state.system.previewMode ? state.system.previewHash : undefined,
        isSpoofed: authState.account.isSpoofed,
        source,
        markupSource: getChannelMarkupSource(state),
      }

      dispatch({
        type: API_CALL,
        api: FETCH_BEDBANK_OFFERS_RATES,
        request: () => getRatesForBedbankOffers(ids, requestData, enablePropertyCollect),
        offerIds: ids,
        filterKey,
      })
    }
  }
}

type ImplicitRoomPricing = {
  inclusive?: number
  memberInclusive?: number
  propertyFees?: number
}
interface Pricing {
  data: Array<ImplicitRoomPricing>
}

export function updateBedbankPricingData(
  offerId: string,
  roomId: string,
  roomRateId: string,
  isFlightBundle: boolean,
  checkIn: string,
  checkOut: string,
  rooms: Array<App.Occupants>,
  pricing: Pricing,
): AppAction {
  return {
    type: BEDBANK_UPDATE_PRICING_DATA,
    data: {
      offerId,
      roomId,
      roomRateId,
      isFlightBundle,
      bedbankRateKey: getBedbankRateKey(rooms, checkIn, checkOut),
      pricing,
    },
  }
}

export function getBedbankCalendarKey(
  region: string,
  duration: number,
  selectedBedbankRoomId?: string | undefined,
) {
  return `${region}-${duration}-${selectedBedbankRoomId ?? 'all'}`
}

export function fetchCalendarForBedbankOffer(
  id: string,
  duration: number,
  selectedBedbankRoomId?: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode

    const filterKey = getBedbankCalendarKey(region, duration, selectedBedbankRoomId)
    if (state.offer.bedbankOfferCalendar[id]?.[filterKey] || state.offer.bedbankOfferCalendarLoading[id]) {
      // already have fetched the calendar for the current filters
      return
    }

    const requestData = {
      propertyId: id,
      region,
      nights: duration,
      roomId: selectedBedbankRoomId,
    }

    dispatch({
      type: API_CALL,
      api: FETCH_BEDBANK_OFFER_CALENDAR,
      request: () => getCalendar(requestData),
      offerId: id,
      filterKey,
    })
  }
}
